import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dumbbell } from "lucide-react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  cn,
  toast,
} from "@cliently/ui";
import {
  isStepCompleted,
  useUpdateProgramAssignmentStep,
  type PortalProgramStep,
} from "../../hooks/use-programs";
import { ProgramBlockMedia } from "./ProgramBlockMedia";

type ExerciseGuidePayload = {
  name: string;
  nameFr: string | null;
  images: string[];
  youtubeUrl: string | null;
  instructions: string[];
  instructionsFr: string[];
  primaryMuscles: string[];
  category: string | null;
  level: string | null;
  equipment: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: PortalProgramStep | null;
  clientId: string;
  onViewExerciseGuide?: (exercise: ExerciseGuidePayload) => void;
  onSaved?: () => void;
};

const Textarea = ({
  className,
  rows,
  ...rest
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea
    rows={rows ?? 3}
    className={cn(
      "border-border/70 bg-background placeholder:text-muted-foreground focus-visible:ring-primary/40 flex w-full rounded-md border px-3 py-2 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50",
      className,
    )}
    {...rest}
  />
);

export const ExerciseStepDetailModal = ({
  open,
  onOpenChange,
  step,
  clientId,
  onViewExerciseGuide,
  onSaved,
}: Props) => {
  const { t } = useTranslation();
  const updateStep = useUpdateProgramAssignmentStep();

  const [isDone, setIsDone] = useState(false);
  const [note, setNote] = useState("");
  const [valueNumber, setValueNumber] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!step) return;
    setIsDone(step.isDone);
    setNote(step.valueText ?? "");
    setValueNumber(step.valueNumber?.toString() ?? "");
  }, [step?.id, step]);

  if (!step) return null;

  const displayName =
    step.exercise?.nameFr?.trim() ||
    step.exercise?.name ||
    step.title?.trim() ||
    t("programs.previewStepUntitled", "Étape sans titre");

  const handleSave = async () => {
    setSaving(true);
    try {
      const data: {
        isDone?: boolean;
        valueText?: string | null;
        valueNumber?: number | null;
      } = {};

      if (step.type === "CHECKBOX") {
        data.isDone = isDone;
        data.valueText = note.trim() ? note.trim() : null;
      } else if (step.type === "TEXT") {
        data.valueText = note.trim() ? note.trim() : null;
      } else if (step.type === "NUMBER" || step.type === "DURATION") {
        const parsed =
          valueNumber.trim() === "" ? null : Number(valueNumber.trim());
        data.valueNumber = Number.isFinite(parsed) ? parsed : null;
        data.valueText = note.trim() ? note.trim() : null;
      }

      await updateStep.mutateAsync({
        id: step.id,
        data,
        assignmentClientId: clientId,
      });
      toast.success(t("programs.exerciseSaved", "Exercice enregistré."));
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error(
        t(
          "programs.exerciseSaveError",
          "Impossible d'enregistrer l'exercice pour le moment.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const completed = isStepCompleted({
    ...step,
    isDone,
    valueText: note,
    valueNumber:
      valueNumber.trim() === "" ? null : Number(valueNumber.trim()) || null,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="w-[calc(100vw-1.5rem)] max-w-lg sm:w-full"
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-base font-bold leading-snug">
              {displayName}
            </DialogTitle>
            {completed ? (
              <span className="bg-emerald-500/15 text-emerald-600 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                {t("programs.exerciseDone", "Validé")}
              </span>
            ) : null}
          </div>
          {step.description?.trim() ? (
            <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
              {step.description}
            </p>
          ) : null}
        </DialogHeader>

        <DialogBody className="space-y-4">
          {step.groupMediaUrl?.trim() ? (
            <ProgramBlockMedia url={step.groupMediaUrl} />
          ) : null}

          {step.exercise && onViewExerciseGuide ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() =>
                onViewExerciseGuide({
                  name: step.exercise!.name,
                  nameFr: step.exercise!.nameFr,
                  images: step.exercise!.images,
                  youtubeUrl: step.exercise!.youtubeUrl,
                  instructions: step.exercise!.instructions,
                  instructionsFr: step.exercise!.instructionsFr,
                  primaryMuscles: step.exercise!.primaryMuscles,
                  category: step.exercise!.category,
                  level: step.exercise!.level,
                  equipment: step.exercise!.equipment,
                })
              }
            >
              <Dumbbell className="h-4 w-4" />
              {t("programs.viewExerciseGuide", "Voir la fiche exercice")}
            </Button>
          ) : null}

          {step.type === "CHECKBOX" ? (
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-3">
              <Checkbox
                checked={isDone}
                onCheckedChange={(checked) => setIsDone(checked === true)}
              />
              <span className="text-sm font-medium">
                {t("programs.markExerciseDone", "Marquer comme réalisé")}
              </span>
            </label>
          ) : null}

          {step.type === "NUMBER" || step.type === "DURATION" ? (
            <div className="space-y-1.5">
              <Label className="text-xs">
                {step.type === "DURATION"
                  ? t("programs.durationValue", "Durée réalisée")
                  : t("programs.numberValue", "Valeur réalisée")}
                {step.targetValue != null ? (
                  <span className="text-muted-foreground ml-1 font-normal">
                    ({t("programs.target", "objectif")} : {step.targetValue}{" "}
                    {step.targetUnit ?? ""})
                  </span>
                ) : null}
              </Label>
              <input
                type="number"
                inputMode="decimal"
                value={valueNumber}
                onChange={(e) => setValueNumber(e.target.value)}
                placeholder={step.targetUnit ?? "0"}
                className="border-border/70 bg-background focus-visible:ring-primary/40 h-10 w-full rounded-md border px-3 text-sm focus-visible:outline-none focus-visible:ring-2"
              />
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label className="text-xs">
              {step.type === "TEXT"
                ? t("programs.exerciseTextResponse", "Ta réponse")
                : t("programs.exerciseNote", "Note")}
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={step.type === "TEXT" ? 4 : 3}
              placeholder={t(
                "programs.exerciseNotePlaceholder",
                "Ressenti, charges, répétitions, remarques…",
              )}
            />
          </div>
        </DialogBody>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close", "Fermer")}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving
              ? t("common.saving", "Enregistrement…")
              : t("common.save", "Enregistrer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
